import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { PageTitle } from "shared/components/layout/PageTitle"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Label } from "shared/ui/label"
import { Input } from "shared/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Button } from "shared/ui/button"
import { Alert, AlertDescription } from "shared/ui/alert"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Textarea } from "shared/ui/textarea"
import { Filter, Search, RotateCcw, Loader2, RefreshCw } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "shared/ui/dropdown-menu"
import {
  PermitIndex,
  getPendingApprovals,
  type PermitListItem,
  type PermitIndexSortField,
} from "modules/permit"
import {
  cancelPermitsBulk,
  getCancelReasons,
  massApprovePermits,
  massRejectPermits,
  type CancelPermitBulkRequestPayload,
  type CancelReasonOption,
} from "modules/permit/api/permitService"
import { DatePicker } from "modules/permit/components/DatePicker"
import { PermitApprovalsBulkActions } from "./PermitApprovalsBulkActions"
import { OpenPermitCancelDialog } from "./open-permit/OpenPermitCancelDialog"
import { getContractorCompanies, type ContractorCompany } from "../api/contractorService"
import { fetchWorkAreas, type WorkAreaOption } from "modules/admin/api/plotPlanService"
import { getAvailablePermitTypes } from "shared/api/templateService"
import { getPersistedSelectedSiteId, resolveTenantId } from "shared/utils/tenantContext"
import { normalizePermitStatus, PERMIT_STATUS_FILTER_OPTIONS } from "modules/permit/constants/permitStatus"

const MASS_APPROVE_ROLE_CODES = new Set(["30005", "30006"])
const MASS_CANCEL_REVISE_ROLE_CODES = new Set(["30003", "30004", "30005", "30006"])

interface LayoutState {
  user?: any
  module?: "E-PTW" | "ADMIN"
  site?: {
    id?: string
    name?: string
    location?: string
  }
  roles?: any[]
  menus?: any[]
}

const APPROVER_ROLE_NAMES = ["Permit Controller", "Shift Supervisor", "Permit Manager", "Permit VP"]
// Backend role IDs that correspond to approval steps
const APPROVER_ROLE_IDS = ["1ffe6c47-be06-4e1e-9019-759648df523a", "e9966d3e-aa43-4a59-8f63-ba4f0975794b"]
const APPROVER_ROLE_MATCHERS = new Set([...APPROVER_ROLE_NAMES, ...APPROVER_ROLE_IDS].map((item) => item.toLowerCase()))
// Deprecated legacy filter list kept temporarily for reference; UI uses ACTIVE_STATUS_FILTERS below.
const STATUS_FILTERS = [
  { value: "ALL", label: "ทั้งหมด" },
  { value: "P0001", label: "ร่าง" },
  { value: "P0010", label: "สร้างใหม่" },
  { value: "P0015", label: "รอการแก้ไขจากผู้สร้าง" },
  { value: "P0251", label: "รอผู้ขอให้ข้อมูลเพิ่มเติม" },
  { value: "P0020", label: "รอผู้ควบคุมงานตรวจสอบ" },
  { value: "P0030", label: "รอหัวหน้ากะตรวจสอบ" },
  { value: "P0040", label: "รอ Shift Manager อนุมัติ" },
  { value: "P0050", label: "รอผู้จัดการอนุมัติ" },
  { value: "P0060", label: "รอผู้จัดการฝ่ายอนุมัติ" },
  { value: "P0070", label: "รอเจ้าของพื้นที่รับ-จ่ายงาน" },
  { value: "P0080", label: "รอการเปิด Permit" },
  { value: "P0008", label: "รอเปิดงาน (ปัจจุบัน)" },
  { value: "P0009", label: "เปิด Permit" },
  { value: "P9000", label: "ปิด Permit" },
  { value: "P9998", label: "ยกเลิก" },
]
const ACTIVE_STATUS_FILTERS = [{ value: "ALL", label: "ทั้งหมด" }, ...PERMIT_STATUS_FILTER_OPTIONS]

const createDefaultFilters = () => ({
  keyword: "",
  statuses: [] as string[],
  permitTypes: [] as string[],
  companyIds: [] as string[],
  workAreaIds: [] as string[],
  dateFrom: "",
  dateTo: "",
})
const PAGE_SIZE_OPTIONS = ["10", "20", "50"]

const normalizeGuid = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  return /^[0-9a-fA-F-]{36}$/.test(trimmed) ? trimmed.toLowerCase() : null
}

const resolveAccessibleLocationIds = (user?: Record<string, any>, roles?: Array<Record<string, any>> | null) => {
  const results = new Set<string>()

  ;[
    user?.locationIds,
    user?.location_ids,
    user?.workAreaIds,
    user?.work_area_ids,
    user?.accessibleLocationIds,
    user?.accessible_location_ids,
    user?.locations,
  ].forEach((candidate) => {
    if (!Array.isArray(candidate)) return
    candidate.forEach((item) => {
      const normalized = normalizeGuid(typeof item === "string" ? item : item?.locationId ?? item?.id)
      if (normalized) {
        results.add(normalized)
      }
    })
  })

  ;(roles ?? []).forEach((role) => {
    const normalized =
      normalizeGuid(role?.departmentId) ??
      normalizeGuid(role?.department_id) ??
      normalizeGuid(role?.locationId) ??
      normalizeGuid(role?.location_id)
    if (normalized) {
      results.add(normalized)
    }
  })

  return Array.from(results)
}


export function PermitApprovalsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = useMemo(() => (location.state as LayoutState | undefined) ?? {}, [location.state])
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const resolvedTenantId = resolveTenantId(state.site?.id, persistedSiteId)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permits, setPermits] = useState<PermitListItem[]>([])
  const [assignmentMap, setAssignmentMap] = useState<Record<string, boolean>>({})
  const [filters, setFilters] = useState(createDefaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortField, setSortField] = useState<PermitIndexSortField>("plannedStart")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedPermitIds, setSelectedPermitIds] = useState<Set<string>>(new Set())
  const [isMassApproveDialogOpen, setIsMassApproveDialogOpen] = useState(false)
  const [isMassApproving, setIsMassApproving] = useState(false)
  const [isMassCancelDialogOpen, setIsMassCancelDialogOpen] = useState(false)
  const [isMassCancelling, setIsMassCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState("")
  const [cancelReasonDetail, setCancelReasonDetail] = useState("")
  const [cancelReasons, setCancelReasons] = useState<CancelReasonOption[]>([])
  const [isMassReviseDialogOpen, setIsMassReviseDialogOpen] = useState(false)
  const [isMassRevising, setIsMassRevising] = useState(false)
  const [reviseNote, setReviseNote] = useState("")
  const [companyOptions, setCompanyOptions] = useState<ContractorCompany[]>([])
  const [companySearchTerm, setCompanySearchTerm] = useState("")
  const [isCompanyLoading, setIsCompanyLoading] = useState(false)
  const [workAreaOptions, setWorkAreaOptions] = useState<WorkAreaOption[]>([])
  const [workAreaSearchTerm, setWorkAreaSearchTerm] = useState("")
  const [isWorkAreaLoading, setIsWorkAreaLoading] = useState(false)
  const [permitTypeOptions, setPermitTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: "ALL", label: "ทั้งหมด" },
  ])

  const isGuidValue = useCallback((value?: string | null) => {
    if (typeof value !== "string") {
      return false
    }
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value.trim())
  }, [])

  const currentUserId = useMemo(() => {
    const candidates: Array<string | undefined | null> = [
      state.user?.id,
      state.user?.user_id,
      state.user?.userId,
      state.user?.emp_id,
      state.user?.empId,
      state.user?.employee_id,
      state.user?.employeeId,
    ]

    let fallback: string | null = null

    for (const candidate of candidates) {
      if (typeof candidate !== "string") {
        continue
      }
      const trimmed = candidate.trim()
      if (!trimmed) {
        continue
      }

      const normalized = trimmed.toLowerCase()
      if (isGuidValue(trimmed)) {
        return normalized
      }
      if (!fallback) {
        fallback = normalized
      }
    }

    return fallback
  }, [isGuidValue, state.user])

  const queryAssignedUserId = useMemo(() => {
    if (!currentUserId) {
      return null
    }
    return isGuidValue(currentUserId) ? currentUserId : null
  }, [currentUserId, isGuidValue])

  // Use roleId (GUID) as the primary token for approval matching
  const currentUserRoleIds = useMemo(() => {
    const normalizedRoles = new Set<string>()

    const addRole = (value: unknown) => {
      if (typeof value !== "string") return
      const trimmed = value.trim()
      if (!trimmed) return
      normalizedRoles.add(trimmed.toLowerCase())
    }

    addRole(state.user?.role_id as string | undefined)
    addRole(state.user?.roleId as string | undefined)
    addRole(state.user?.role?.id as string | undefined)

    const userRoles =
      (Array.isArray(state.user?.roles) && state.user.roles) ||
      (Array.isArray(state.user?.user_roles) && state.user.user_roles) ||
      []

    for (const role of userRoles as Array<any>) {
      addRole(role?.roleId ?? role?.role_id ?? role?.id)
    }

    const tenantRoles = Array.isArray(state.roles) ? state.roles : []
    for (const role of tenantRoles as Array<any>) {
      addRole(role?.roleId ?? role?.role_id ?? role?.id)
    }

    return normalizedRoles
  }, [state.roles, state.user])

  const accessibleLocationIds = useMemo(
    () => resolveAccessibleLocationIds(state.user, Array.isArray(state.roles) ? state.roles : []),
    [state.roles, state.user],
  )

  const canApprove = useMemo(() => {
    // If user holds any approver roleId or matches menu access, allow approval page.
    const hasApproverRole = Array.from(currentUserRoleIds).some((token) =>
      APPROVER_ROLE_MATCHERS.has(token),
    )
    if (hasApproverRole) {
      return true
    }

    const hasApprovalMenuAccess =
      Array.isArray(state.menus) &&
      state.menus.some((menu: any) => {
        const path = typeof menu.routePath === "string" ? menu.routePath.toLowerCase() : ""
        const code = typeof menu.code === "string" ? menu.code.toLowerCase() : ""
        const menuId = typeof menu.menuId === "string" ? menu.menuId.toLowerCase() : ""
        return path === "/permit-approvals" || code === "permit-approvals" || menuId === "permit-approvals"
      })

    return hasApprovalMenuAccess
  }, [currentUserRoleIds, state.menus])

  const filteredCompanies = useMemo(() => {
    const term = companySearchTerm.trim().toLowerCase()
    if (!term) {
      return companyOptions
    }
    return companyOptions.filter((company) => {
      const name = company.companyName?.toLowerCase() ?? ""
      const tax = company.taxId?.toLowerCase() ?? ""
      return name.includes(term) || tax.includes(term)
    })
  }, [companyOptions, companySearchTerm])

  const filteredWorkAreas = useMemo(() => {
    const term = workAreaSearchTerm.trim().toLowerCase()
    if (!term) {
      return workAreaOptions
    }
    return workAreaOptions.filter((item) => {
      const name = item.name?.toLowerCase() ?? ""
      const code = item.code?.toLowerCase() ?? ""
      return name.includes(term) || code.includes(term)
    })
  }, [workAreaOptions, workAreaSearchTerm])

  useEffect(() => {
    let cancelled = false
    setIsCompanyLoading(true)
    getContractorCompanies({ tenantId: resolvedTenantId, pageNumber: 1, pageSize: 1000 })
      .then((result) => {
        if (cancelled) return
        setCompanyOptions(result.items ?? [])
      })
      .catch((error) => {
        console.error("ไม่สามารถโหลดรายชื่อบริษัทได้", error)
        if (!cancelled) {
          setCompanyOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCompanyLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [resolvedTenantId])

  useEffect(() => {
    let cancelled = false
    getAvailablePermitTypes(resolvedTenantId)
      .then((items) => {
        if (cancelled) return
        const options =
          items?.map((item) => ({
            value: item.permitCode || item.permitType || "",
            label: item.permitType || item.permitCode || "",
          })) ?? []
        setPermitTypeOptions([{ value: "ALL", label: "ทั้งหมด" }, ...options.filter((opt) => opt.value)])
      })
      .catch(() => {
        if (cancelled) return
        setPermitTypeOptions([
          { value: "ALL", label: "ทั้งหมด" },
          { value: "Hot Work", label: "Hot Work" },
          { value: "Cold Work", label: "Cold Work" },
          { value: "Confine Work Space", label: "Confine Work Space" },
          { value: "Excavation", label: "Excavation" },
        ])
      })

    return () => {
      cancelled = true
    }
  }, [resolvedTenantId])

  useEffect(() => {
    let cancelled = false
    setIsWorkAreaLoading(true)
    fetchWorkAreas(resolvedTenantId)
      .then((items) => {
        if (cancelled) return
        const sourceItems = items ?? []
        const nextItems =
          accessibleLocationIds.length > 0
            ? sourceItems.filter((item) => accessibleLocationIds.includes(item.locationId.toLowerCase()))
            : sourceItems
        setWorkAreaOptions(nextItems)
      })
      .catch((error) => {
        console.error("ไม่สามารถโหลดพื้นที่ปฏิบัติงานได้", error)
        if (!cancelled) {
          setWorkAreaOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsWorkAreaLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [accessibleLocationIds, resolvedTenantId])

  const handleNavigate = useCallback(
    (page: string) => {
      switch (page) {
        case "home":
          navigate("/dashboard", { state })
          break
        case "permit-index":
          navigate("/permit-index", { state })
          break
        default:
          navigate("/dashboard", { state })
      }
    },
    [navigate, state],
  )

  const fetchPendingPermits = useCallback(async () => {
    if (!canApprove) {
      setPermits([])
      setAssignmentMap({})
      setTotalItems(0)
      setTotalPages(1)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const selectedLocationIds = appliedFilters.workAreaIds.length > 0 ? appliedFilters.workAreaIds : undefined

      const normalizedStatuses = Array.from(
        new Set(
          (appliedFilters.statuses ?? [])
            .map((status) => status?.trim())
            .filter((status): status is string => Boolean(status) && status.toUpperCase() !== "ALL")
            .map((status) => normalizePermitStatus(status) ?? status.toUpperCase()),
        ),
      )

      const response = await getPendingApprovals({
        tenantId: resolvedTenantId,
        pageNumber,
        pageSize,
        sortField,
        sortDirection,
        assignedToUserId: queryAssignedUserId ?? undefined,
        statuses: normalizedStatuses,
        permitTypes: appliedFilters.permitTypes,
        companyIds: appliedFilters.companyIds,
        locationIds: selectedLocationIds,
        searchTerm: appliedFilters.keyword,
        plannedStartFrom: appliedFilters.dateFrom || undefined,
        plannedStartTo: appliedFilters.dateTo || undefined,
      })
      const nextPermits = response.items ?? []
      setPermits(nextPermits)
      setAssignmentMap(Object.fromEntries(nextPermits.map((permit) => [permit.permitId, true])))
      setSelectedPermitIds(new Set())
      const safeTotalPages = Math.max(response.totalPages ?? 1, 1)
      const safeTotalItems = response.totalItems ?? nextPermits.length
      setTotalItems(safeTotalItems)
      setTotalPages(safeTotalPages <= 1 ? 1 : safeTotalPages)
      if (pageNumber > safeTotalPages) {
        setPageNumber(safeTotalPages)
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const message =
          (err.response?.data as { message?: string } | undefined)?.message ??
          err.response?.statusText ??
          err.message
        setError(message || "ไม่สามารถโหลดรายการอนุมัติได้")
      } else {
        setError("ไม่สามารถโหลดรายการอนุมัติได้")
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    appliedFilters.companyIds,
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.keyword,
    appliedFilters.permitTypes,
    appliedFilters.statuses,
    appliedFilters.workAreaIds,
    accessibleLocationIds,
    canApprove,
    pageNumber,
    pageSize,
    queryAssignedUserId,
    resolvedTenantId,
    sortDirection,
    sortField,
  ])

  useEffect(() => {
    void fetchPendingPermits()
  }, [fetchPendingPermits])

  useEffect(() => {
    let cancelled = false
    getCancelReasons(resolvedTenantId)
      .then((items) => {
        if (!cancelled) setCancelReasons(items ?? [])
      })
      .catch((error) => {
        console.error("PermitApprovalsPage: failed to load cancel reasons", error)
        if (!cancelled) setCancelReasons([])
      })
    return () => {
      cancelled = true
    }
  }, [resolvedTenantId])

  const canMassApprove = useMemo(() => {
    const roles = Array.isArray(state.roles) ? state.roles : []
    return roles.some((role: any) => {
      const code = typeof role?.code === "string" ? role.code.trim() : null
      return code ? MASS_APPROVE_ROLE_CODES.has(code) : false
    })
  }, [state.roles])

  const canMassCancelOrRevise = useMemo(() => {
    const roles = Array.isArray(state.roles) ? state.roles : []
    return roles.some((role: any) => {
      const code = typeof role?.code === "string" ? role.code.trim() : null
      return code ? MASS_CANCEL_REVISE_ROLE_CODES.has(code) : false
    })
  }, [state.roles])

  const canUseMassActions = canMassApprove || canMassCancelOrRevise

  const pendingPermits = useMemo(() => {
    if (!canApprove) {
      return []
    }

    return permits.filter((permit) => assignmentMap[permit.permitId] === true)
  }, [assignmentMap, canApprove, permits])

  const selectablePermits = pendingPermits
  const isAllSelected = selectablePermits.length > 0 && selectedPermitIds.size === selectablePermits.length
  const selectedPermits = useMemo(
    () => pendingPermits.filter((permit) => selectedPermitIds.has(permit.permitId)),
    [pendingPermits, selectedPermitIds],
  )
  const canApproveSelection =
    selectedPermits.length > 0 && selectedPermits.every((permit) => permit.permitType.toLowerCase().includes("hot work"))

  const handleToggleSelect = useCallback((permitId: string, checked: boolean) => {
    setSelectedPermitIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(permitId)
      } else {
        next.delete(permitId)
      }
      return next
    })
  }, [])

  const handleMassApprove = useCallback(async () => {
    if (selectedPermitIds.size === 0 || !queryAssignedUserId || !canApproveSelection) return
    setIsMassApproving(true)
    try {
      const result = await massApprovePermits(
        resolvedTenantId,
        queryAssignedUserId,
        Array.from(selectedPermitIds),
      )
      if (result.succeededCount > 0) {
        toast.success(`อนุมัติสำเร็จ ${result.succeededCount} รายการ`)
      }
      if (result.failedCount > 0) {
        toast.error(`อนุมัติไม่สำเร็จ ${result.failedCount} รายการ`)
      }
      setSelectedPermitIds(new Set())
      setIsMassApproveDialogOpen(false)
      void fetchPendingPermits()
    } catch {
      toast.error("เกิดข้อผิดพลาดในการ Mass Approve")
    } finally {
      setIsMassApproving(false)
    }
  }, [canApproveSelection, fetchPendingPermits, queryAssignedUserId, resolvedTenantId, selectedPermitIds])

  const toggleValue = (list: string[], value: string, checked: boolean) => {
    if (checked) {
      if (list.includes(value)) return list
      return [...list, value]
    }
    return list.filter((item) => item !== value)
  }

  const selectedCompanies = useMemo(
    () => companyOptions.filter((company) => filters.companyIds.includes(company.companyId)),
    [companyOptions, filters.companyIds],
  )

  const selectedWorkAreas = useMemo(
    () => workAreaOptions.filter((item) => filters.workAreaIds.includes(item.locationId)),
    [filters.workAreaIds, workAreaOptions],
  )

  const renderSelectedSummary = (items: { label: string }[]) => {
    if (items.length === 0) return "ทั้งหมด"
    if (items.length === 1) return items[0].label
    return `${items[0].label} +${items.length - 1}`
  }

  const handleSearch = () => {
    setPageNumber(1)
    setAppliedFilters(filters)
  }

  const handleClearFilters = () => {
    const reset = createDefaultFilters()
    setFilters(reset)
    setAppliedFilters(reset)
    setPageNumber(1)
    setPageSize(10)
  }

  const handleToggleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedPermitIds(checked ? new Set(selectablePermits.map((permit) => permit.permitId)) : new Set())
    },
    [selectablePermits],
  )

  const handleMassCancel = useCallback(async () => {
    if (selectedPermitIds.size === 0 || !cancelReason) return
    setIsMassCancelling(true)
    try {
      const payload: CancelPermitBulkRequestPayload = {
        tenantId: resolvedTenantId,
        permitIds: Array.from(selectedPermitIds),
        cancelledBy: queryAssignedUserId,
        reasonCode: cancelReason,
        reasonDetail: cancelReasonDetail || null,
      }
      await cancelPermitsBulk(payload)
      toast.success(`ยกเลิกสำเร็จ ${selectedPermitIds.size} รายการ`)
      setSelectedPermitIds(new Set())
      setCancelReason("")
      setCancelReasonDetail("")
      setIsMassCancelDialogOpen(false)
      void fetchPendingPermits()
    } catch {
      toast.error("เกิดข้อผิดพลาดในการ Mass Cancel")
    } finally {
      setIsMassCancelling(false)
    }
  }, [cancelReason, cancelReasonDetail, fetchPendingPermits, queryAssignedUserId, resolvedTenantId, selectedPermitIds])

  const handleMassRevise = useCallback(async () => {
    if (selectedPermitIds.size === 0 || !reviseNote.trim() || !queryAssignedUserId) return
    setIsMassRevising(true)
    try {
      const result = await massRejectPermits(
        resolvedTenantId,
        queryAssignedUserId,
        Array.from(selectedPermitIds),
        reviseNote.trim(),
      )
      if (result.succeededCount > 0) {
        toast.success(`ส่งกลับแก้ไขสำเร็จ ${result.succeededCount} รายการ`)
      }
      if (result.failedCount > 0) {
        toast.error(`ส่งกลับแก้ไขไม่สำเร็จ ${result.failedCount} รายการ`)
      }
      setSelectedPermitIds(new Set())
      setReviseNote("")
      setIsMassReviseDialogOpen(false)
      void fetchPendingPermits()
    } catch {
      toast.error("เกิดข้อผิดพลาดในการส่งกลับแก้ไข")
    } finally {
      setIsMassRevising(false)
    }
  }, [fetchPendingPermits, queryAssignedUserId, resolvedTenantId, reviseNote, selectedPermitIds])

  const handleSort = useCallback((field: PermitIndexSortField) => {
    setPageNumber(1)
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) => (currentDirection === "asc" ? "desc" : "asc"))
        return currentField
      }

      setSortDirection("asc")
      return field
    })
  }, [])

  const handleViewPermit = (permitId: string) => {
    navigate("/permit-request", {
      state: {
        ...state,
        mode: "edit",
        permitId,
        forceReadOnly: true,
        hideActionBar: true,
        entryMode: "display",
      },
    })
  }

  const handlePageChange = (nextPage: number) => {
    if (nextPage >= 1 && nextPage <= totalPages) {
      setPageNumber(nextPage)
    }
  }

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value) || 10
    setPageSize(nextSize)
    setPageNumber(1)
  }

  const startIndex = totalItems === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const endIndex = Math.min(pageNumber * pageSize, totalItems)

  const handleOpenPermit = (permitId: string) => {
    navigate("/permit-request", {
      state: {
        ...state,
        mode: "edit",
        permitId,
        entryMode: "approve",
        hideActionBar: false,
        forceReadOnly: false,
      },
    })
  }

  return (
    <div className="space-y-6">
      <PageTitle currentPage="approve-permit" onNavigate={handleNavigate} />

      {!canApprove ? (
        <Alert variant="destructive">
          <AlertDescription>
            บัญชีผู้ใช้ของคุณไม่มีสิทธิ์ในการอนุมัติเอกสาร หากคิดว่ามีข้อผิดพลาดโปรดติดต่อผู้ดูแลระบบ
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="overflow-hidden border border-blue-200 shadow-sm">
        <CardHeader className="border-b border-blue-100 bg-blue-50/70 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
                <Filter className="h-5 w-5 text-blue-600" />
                เงื่อนไขการค้นหา
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void fetchPendingPermits()
              }}
              disabled={isLoading || !canApprove}
              className="shrink-0"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  กำลังโหลด...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  รีเฟรช
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="bg-blue-50/50 p-3">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              handleSearch()
            }}
            className="space-y-5"
          >
            <div className="grid gap-4 lg:grid-cols-12">
              <div className="lg:col-span-3">
                <Label htmlFor="keyword">คำค้นหา</Label>
                <div className="relative mt-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="keyword"
                    placeholder="เลขที่หรือรายละเอียดงาน"
                    value={filters.keyword}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, keyword: event.target.value }))
                    }
                    className="bg-white pl-9"
                  />
                </div>
              </div>
              <div className="lg:col-span-2">
                <Label htmlFor="status">สถานะเอกสาร</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                      <span>
                        {renderSelectedSummary(
                          (filters.statuses.length === 0
                            ? []
                            : ACTIVE_STATUS_FILTERS.filter((s) => filters.statuses.includes(s.value))
                          ).map((s) => ({ label: s.label })),
                        )}
                      </span>
                      <span className="text-xs text-slate-500">เลือกได้หลายค่า</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 max-h-80 overflow-y-auto">
                    <DropdownMenuLabel>สถานะเอกสาร</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={filters.statuses.length === 0}
                      onCheckedChange={() => setFilters((prev) => ({ ...prev, statuses: [] }))}
                      onSelect={(event) => event.preventDefault()}
                    >
                      ทั้งหมด
                    </DropdownMenuCheckboxItem>
                    {ACTIVE_STATUS_FILTERS.filter((status) => status.value !== "ALL").map((status) => (
                      <DropdownMenuCheckboxItem
                        key={status.value}
                        checked={filters.statuses.includes(status.value)}
                        onCheckedChange={(checked) =>
                          setFilters((prev) => ({
                            ...prev,
                            statuses: toggleValue(prev.statuses, status.value, checked === true),
                          }))
                        }
                        onSelect={(event) => event.preventDefault()}
                      >
                        {status.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="lg:col-span-2">
                <Label htmlFor="company">ชื่อบริษัท</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                      <span>
                        {renderSelectedSummary(
                          selectedCompanies.map((company) => ({ label: company.companyName })),
                        )}
                      </span>
                      <span className="text-xs text-slate-500">
                        {filters.companyIds.length === 0 ? "ทั้งหมด" : `${filters.companyIds.length} บริษัท`}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2">
                      <Input
                        placeholder="ค้นหาบริษัท..."
                        value={companySearchTerm}
                        onChange={(event) => setCompanySearchTerm(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        className="h-8"
                      />
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={filters.companyIds.length === 0}
                      onCheckedChange={() => setFilters((prev) => ({ ...prev, companyIds: [] }))}
                      onSelect={(event) => event.preventDefault()}
                    >
                      ทั้งหมด
                    </DropdownMenuCheckboxItem>
                    {isCompanyLoading ? (
                      <DropdownMenuCheckboxItem disabled checked={false}>
                        กำลังโหลด...
                      </DropdownMenuCheckboxItem>
                    ) : null}
                    {!isCompanyLoading && filteredCompanies.length === 0 ? (
                      <DropdownMenuCheckboxItem disabled checked={false}>
                        ไม่พบบริษัท
                      </DropdownMenuCheckboxItem>
                    ) : null}
                    {filteredCompanies.map((company) => (
                      <DropdownMenuCheckboxItem
                        key={company.companyId}
                        checked={filters.companyIds.includes(company.companyId)}
                        onCheckedChange={(checked) =>
                          setFilters((prev) => ({
                            ...prev,
                            companyIds: toggleValue(prev.companyIds, company.companyId, checked === true),
                          }))
                        }
                        onSelect={(event) => event.preventDefault()}
                      >
                        {company.companyName}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="lg:col-span-2">
                <Label htmlFor="permitType">ประเภท Permit</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                      <span>
                        {renderSelectedSummary(
                          (filters.permitTypes.length === 0
                            ? []
                            : permitTypeOptions.filter((p) => filters.permitTypes.includes(p.value))
                          ).map((p) => ({ label: p.label })),
                        )}
                      </span>
                      <span className="text-xs text-slate-500">เลือกได้หลายค่า</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 max-h-80 overflow-y-auto">
                    <DropdownMenuLabel>ประเภท Permit</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={filters.permitTypes.length === 0}
                      onCheckedChange={() => setFilters((prev) => ({ ...prev, permitTypes: [] }))}
                      onSelect={(event) => event.preventDefault()}
                    >
                      ทั้งหมด
                    </DropdownMenuCheckboxItem>
                    {permitTypeOptions
                      .filter((type) => type.value.toUpperCase() !== "ALL")
                      .map((type) => (
                        <DropdownMenuCheckboxItem
                          key={type.value}
                          checked={filters.permitTypes.includes(type.value)}
                          onCheckedChange={(checked) =>
                            setFilters((prev) => ({
                              ...prev,
                              permitTypes: toggleValue(prev.permitTypes, type.value, checked === true),
                            }))
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {type.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="lg:col-span-3">
                <Label htmlFor="workArea">พื้นที่ปฏิบัติงาน</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="mt-1 w-full justify-between bg-white">
                      <span>
                        {renderSelectedSummary(
                          selectedWorkAreas.map((item) => ({ label: item.name || item.code || item.locationId })),
                        )}
                      </span>
                      <span className="text-xs text-slate-500">
                        {filters.workAreaIds.length === 0 ? "ทั้งหมด" : `${filters.workAreaIds.length} พื้นที่`}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2">
                      <Input
                        placeholder="ค้นหาพื้นที่..."
                        value={workAreaSearchTerm}
                        onChange={(event) => setWorkAreaSearchTerm(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        className="h-8"
                      />
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={filters.workAreaIds.length === 0}
                      onCheckedChange={() => setFilters((prev) => ({ ...prev, workAreaIds: [] }))}
                      onSelect={(event) => event.preventDefault()}
                    >
                      ทั้งหมด
                    </DropdownMenuCheckboxItem>
                    {isWorkAreaLoading ? (
                      <DropdownMenuCheckboxItem disabled checked={false}>
                        กำลังโหลด...
                      </DropdownMenuCheckboxItem>
                    ) : null}
                    {!isWorkAreaLoading && filteredWorkAreas.length === 0 ? (
                      <DropdownMenuCheckboxItem disabled checked={false}>
                        ไม่พบพื้นที่ปฏิบัติงาน
                      </DropdownMenuCheckboxItem>
                    ) : null}
                    {filteredWorkAreas.map((item) => (
                      <DropdownMenuCheckboxItem
                        key={item.locationId}
                        checked={filters.workAreaIds.includes(item.locationId)}
                        onCheckedChange={(checked) =>
                          setFilters((prev) => ({
                            ...prev,
                            workAreaIds: toggleValue(prev.workAreaIds, item.locationId, checked === true),
                          }))
                        }
                        onSelect={(event) => event.preventDefault()}
                      >
                        {item.name || item.code}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-12">
              <div className="lg:col-span-3">
                <Label htmlFor="dateFrom">วันที่เริ่มต้น</Label>
                <DatePicker
                  id="dateFrom"
                  value={filters.dateFrom || ""}
                  onChange={(date) => setFilters((prev) => ({ ...prev, dateFrom: date }))}
                  placeholder="เลือกวันที่"
                  className="mt-1"
                  allowPastDates
                />
              </div>
              <div className="lg:col-span-3">
                <Label htmlFor="dateTo">วันที่สิ้นสุด</Label>
                <DatePicker
                  id="dateTo"
                  value={filters.dateTo || ""}
                  onChange={(date) => setFilters((prev) => ({ ...prev, dateTo: date }))}
                  placeholder="เลือกวันที่"
                  className="mt-1"
                  allowPastDates
                />
              </div>
              <div className="lg:col-span-6 flex items-center justify-end gap-3">
                <Button type="button" variant="outline" onClick={handleClearFilters}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  ล้างข้อมูล
                </Button>
                <Button type="submit" className="bg-blue-600 text-white hover:bg-blue-700">
                  <Search className="mr-2 h-4 w-4" />
                  ค้นหา
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {canUseMassActions ? (
        <PermitApprovalsBulkActions
          isAllSelected={isAllSelected}
          selectedCount={selectedPermitIds.size}
          selectableCount={selectablePermits.length}
          canMassApprove={canMassApprove}
          canApproveSelection={canApproveSelection}
          canMassCancelOrRevise={canMassCancelOrRevise}
          onToggleSelectAll={handleToggleSelectAll}
          onMassCancel={() => setIsMassCancelDialogOpen(true)}
          onMassApprove={() => setIsMassApproveDialogOpen(true)}
          onMassRevise={() => setIsMassReviseDialogOpen(true)}
        />
      ) : null}

      <PermitIndex
        permits={pendingPermits}
        isLoading={isLoading}
        error={error}
        onViewPermit={handleViewPermit}
        onEditPermit={handleOpenPermit}
        mode="approval"
        tenantId={resolvedTenantId}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        canMassSelect={canUseMassActions}
        selectedPermitIds={selectedPermitIds}
        onToggleSelect={handleToggleSelect}
        locationLabelMap={workAreaOptions.reduce<Record<string, string>>((acc, item) => {
          if (item.locationId) {
            acc[item.locationId] = item.name || item.code || item.locationId
          }
          if (item.code) {
            acc[item.code] = item.name || item.code
          }
          return acc
        }, {})}
      />

      <Dialog open={isMassApproveDialogOpen} onOpenChange={setIsMassApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ยืนยัน Mass Approve</DialogTitle>
            <DialogDescription>
              คุณต้องการอนุมัติ {selectedPermitIds.size} รายการที่เลือกใช่หรือไม่?
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMassApproveDialogOpen(false)} disabled={isMassApproving}>
              ยกเลิก
            </Button>
            <Button
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={() => { void handleMassApprove() }}
              disabled={isMassApproving}
            >
              {isMassApproving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />กำลังอนุมัติ...</> : "ยืนยัน"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OpenPermitCancelDialog
        open={isMassCancelDialogOpen}
        permitIds={Array.from(selectedPermitIds)}
        permits={selectedPermits}
        cancelReason={cancelReason}
        cancelReasonDetail={cancelReasonDetail}
        cancelReasons={cancelReasons}
        isSubmitting={isMassCancelling}
        onOpenChange={setIsMassCancelDialogOpen}
        onCancelReasonChange={setCancelReason}
        onCancelReasonDetailChange={setCancelReasonDetail}
        onSubmit={() => { void handleMassCancel() }}
      />

      <Dialog open={isMassReviseDialogOpen} onOpenChange={setIsMassReviseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ส่งกลับให้ผู้ขอแก้ไข</DialogTitle>
            <DialogDescription>
              ระบบจะส่งใบงานที่เลือก {selectedPermitIds.size} รายการกลับให้ผู้ขอแก้ไข และผู้ขอต้องส่งอนุมัติใหม่ตาม flow เดิม
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="mass-revise-note">เหตุผลในการส่งกลับ *</Label>
            <Textarea
              id="mass-revise-note"
              value={reviseNote}
              onChange={(event) => setReviseNote(event.target.value)}
              placeholder="กรุณาระบุเหตุผลที่ต้องการให้แก้ไข"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMassReviseDialogOpen(false)} disabled={isMassRevising}>
              ยกเลิก
            </Button>
            <Button
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={() => { void handleMassRevise() }}
              disabled={isMassRevising || !reviseNote.trim()}
            >
              {isMassRevising ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />กำลังส่งกลับ...</> : "ยืนยันส่งกลับ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-amber-200 p-3">
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between p-0">
          <div className="text-sm text-gray-600">
            แสดง {startIndex === 0 ? 0 : startIndex} - {endIndex} จากทั้งหมด {totalItems} รายการ
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>จำนวนต่อหน้า</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="h-9 w-24 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => handlePageChange(1)}
              >
                หน้าแรก
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => handlePageChange(pageNumber - 1)}
              >
                ก่อนหน้า
              </Button>
              <span className="text-sm text-gray-600">
                หน้า {pageNumber} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => handlePageChange(pageNumber + 1)}
              >
                ถัดไป
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => handlePageChange(totalPages)}
              >
                หน้าสุดท้าย
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
